<template>
  <div class="review-media-viewer">
    <!-- Processing banner: shown while we play the pristine original as a
         preview because the HD transcode hasn't produced a variant yet. Full
         width, sits directly above the player. Clears the moment the source
         swaps to the encoded MP4. -->
    <div v-if="isProcessingPreview" class="processing-banner">
      <BrandLoader :size="'2x'" />
      <span class="processing-banner__text">
        <strong>Processing HD…</strong>
        Showing your original upload — playback may buffer, seek slowly, or
        briefly interrupt until encoding finishes.
      </span>
    </div>
    <div
      class="media-container"
      :style="{ aspectRatio }"
      @click="togglePlayback"
    >
      <!-- Video -->
      <template v-if="item.type === 'video'">
        <video
          v-if="currentUrl"
          ref="videoRef"
          class="media-element"
          :src="usesHlsJs ? undefined : currentUrl"
          :poster="item.poster || undefined"
          preload="metadata"
          :crossorigin="crossorigin"
          playsinline
          @timeupdate="onTimeUpdate"
          @play="onPlay"
          @pause="trail('pause'); emit('pause')"
          @loadedmetadata="trail('loadedmetadata'); onLoadedMetadata()"
          @durationchange="emitDurationIfUsable"
          @seeked="trail('seeked'); onSeeked()"
          @ended="trail('ended'); emit('pause')"
          @waiting="trail('waiting'); isBuffering = true"
          @canplay="trail('canplay'); isBuffering = false"
          @loadstart="trail('loadstart')"
          @stalled="trail('stalled')"
          @playing="trail('playing')"
          @seeking="trail('seeking')"
          @abort="trail('abort')"
          @emptied="trail('emptied')"
          @error="trail('error'); onVideoError()"
        />
        <img
          v-if="item.poster && !hasStarted && !loadError"
          :src="item.poster"
          class="poster-overlay"
        />
        <div v-if="isTranscoding" class="transcoding-overlay" @click.stop>
          <BrandLoader :size="'3x'" />
          <p class="transcoding-title">Processing video…</p>
        </div>
        <div v-if="loadError && !isTranscoding && currentUrl" class="error-overlay" @click.stop>
          <span class="icon is-large">
            <font-awesome-icon
              icon="fa-light fa-circle-exclamation"
              size="3x"
            />
          </span>
          <p class="error-title">
            {{ errorKind === 'decode' ? 'This video can’t be played' : errorKind === 'failed' ? 'Processing failed' : 'Video failed to load' }}
          </p>
          <p v-if="errorKind === 'failed'" class="error-detail">
            This version couldn’t be processed for playback.
            Please re-upload it, or let the team know so they can replace it.
          </p>
          <p v-else-if="errorKind === 'decode'" class="error-detail">
            Your browser couldn’t decode this video.
            Reload the page to try again. If it keeps happening,
            please let the team know.
          </p>
          <p v-else class="error-detail">
            Check your network connection or try a different browser.
            If the problem persists on another network (such as a mobile hotspot),
            your firewall or content blocker may be blocking video playback.
          </p>
        </div>
        <div v-if="!isPlaying && !loadError && !isReversing" class="play-overlay">
          <span class="icon is-large">
            <font-awesome-icon
              icon="fa-duotone fa-light fa-play"
              size="3x"
            />
          </span>
        </div>
        <div v-if="isBuffering && isPlaying" class="buffering-overlay">
          <BrandLoader :size="'3x'" />
        </div>
        <div v-if="isShuttling" class="shuttle-badge">
          <font-awesome-icon
            icon="fa-solid fa-backward"
            active-icon="fa-solid fa-forward"
            :active="!isReversing"
          />
          <span>{{ Math.abs(shuttleSpeed) }}x</span>
        </div>
      </template>

      <!-- Image -->
      <template v-else>
        <img
          v-if="!isTranscoding && currentUrl"
          class="media-element"
          :src="currentUrl"
          :alt="item.name"
        />
        <div v-else-if="isTranscoding" class="transcoding-overlay" @click.stop>
          <BrandLoader :size="'3x'" />
          <p class="transcoding-title">Processing…</p>
        </div>
      </template>

    </div>
  </div>
</template>

<script setup>
import BrandLoader from '@/components/global/BrandLoader.vue';
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { logger } from '@/services/loggerService'
import { useVideoShuttle } from '@/composables/useVideoShuttle'
import { needsHlsJs, attachHlsJs } from '@/utils/hlsSource'
import { BUNNY_STREAM_STATUS } from '@/utils/bunnyConstants'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  isPlaying: {
    type: Boolean,
    default: false,
  },
  volume: {
    type: Number,
    default: 1,
  },
  isMuted: {
    type: Boolean,
    default: false,
  },
  playbackRate: {
    type: Number,
    default: 1,
  },
  // Pass `null` to omit the attribute entirely — required for sources whose
  // CDN doesn't send Access-Control-Allow-Origin (e.g. Bunny Stream). Default
  // 'anonymous' preserves existing review-tool behavior on Firebase URLs.
  crossorigin: {
    type: String,
    default: 'anonymous',
  },
  // Aspect ratio of the player frame, expressed as a CSS aspect-ratio
  // value (e.g. '16 / 9' for landscape, '9 / 16' for portrait/vertical).
  // Defaults to 16:9 — callers that know the source is vertical should
  // pass the actual ratio so the player frame matches the video and we
  // don't letterbox unnecessarily.
  aspectRatio: {
    type: String,
    default: '16 / 9',
  },
})

const emit = defineEmits(['timeupdate', 'play', 'pause', 'duration-change', 'refresh-url'])

const videoRef = ref(null)
const isBuffering = ref(false)
const hasStarted = ref(false)
const loadError = ref(false)
// 'decode' = corrupt/undecodable file (not the user's fault), 'generic' = otherwise.
// Drives which explanation the error overlay shows.
const errorKind = ref('generic')
const currentUrl = ref(props.item?.url || '')
// Bunny playback is an HLS playlist. Native-HLS browsers take it as `src`;
// the rest attach it through hls.js, which owns the element's source.
const usesHlsJs = computed(() => needsHlsJs(currentUrl.value))
let detachHls = null
let hlsAttachSeq = 0
// Reload the current source: a plain load() re-reads `src`, but an hls.js
// element has no `src` (load() would wipe the MediaSource), so re-attach.
async function reloadSource() {
  if (!videoRef.value) return
  detachHls?.()
  detachHls = null
  if (!usesHlsJs.value) {
    videoRef.value.load()
    return
  }
  const seq = ++hlsAttachSeq
  const detach = await attachHlsJs(videoRef.value, currentUrl.value, onHlsFatal)
  if (seq !== hlsAttachSeq) detach() // superseded while hls.js loaded
  else detachHls = detach
}
// A transient load failure on a Bunny video is almost always a freshly-
// transcoded variant the serving CDN edge hasn't pulled yet — reloading the (still-valid) signed URL after a short
// backoff re-issues the GET once the edge has warmed. Capped so a genuinely
// dead source still surfaces the error overlay instead of looping.
const MAX_CDN_RETRIES = 3
const cdnRetryCount = ref(0)
// Same-URL CDN reloads (above) recover a cold edge. If those are exhausted and
// the Bunny source still won't load, the signed token has likely EXPIRED — the
// URL itself is dead, so reloading it again can't help. Ask the parent (which
// owns getBunnyReviewVideoUrl resolution) to mint a FRESH signed URL via
// 'refresh-url'. `awaitingRemint` routes the resulting props.item.url change
// through the position-restoring reload instead of the generic "new clip
// selected" reset. Capped so a genuinely dead source surfaces the error overlay
// instead of looping emit → remint → fail → emit.
const MAX_REMINTS = 2
const remintCount = ref(0)
const awaitingRemint = ref(false)
// When we swap to a refreshed URL we lose the element's playback position
// (load() resets to 0). Stash where we were so onLoadedMetadata can restore it.
const pendingSeek = ref(null)
// Some encodes report duration === Infinity until the file is scanned. We probe
// once per item by seeking to the end; `needsProbeReset` flags that the element's
// position must be restored (and transient timeupdates suppressed) until it lands.
const hasProbedInfinity = ref(false)
const needsProbeReset = ref(false)
// Where to put the playhead back once the probe settles. Retargeted by a
// user seek made mid-probe so their click wins over the pre-probe position.
let probeResumeTime = 0
let probeTimer = null
const PROBE_TIMEOUT_MS = 2500
// Safety net for the re-mint request: if the parent never answers 'refresh-url'
// with a usable URL (callable failed, network down, or it bailed silently),
// props.item.url never changes and the position-restoring reload never fires —
// leaving the player spinning forever. Surface the error overlay once this
// elapses. Generous on purpose: on the public share route the "re-mint" is a
// whole getPublicReviewData round trip, and a cold callable there routinely
// beats 10s — firing that early showed a spurious error on a clip that then
// loaded fine.
let remintTimer = null
// When the last fresh URL landed. `expires` has 1s resolution, so a re-mint in
// the same second returns the identical URL and the url watcher never fires.
let lastRemintArrivedAt = 0
const REMINT_TIMEOUT_MS = 30000
// A paused video in a hidden tab still errors once its 2h token dies (native
// HLS keeps polling segments). Nobody is watching, so don't remint then — flag
// it and mint when the tab comes back or play is pressed.
const needsFreshUrl = ref(false)
const shouldDeferRemint = () => !props.isPlaying && typeof document !== 'undefined' && document.hidden && isUrlExpired(currentUrl.value)
function deferRemint() {
  needsFreshUrl.value = true
  loadError.value = false
  isBuffering.value = false
  trail('remint-deferred')
}
// Bunny's HLS token is a path token over /{videoId}/, so it also authorizes the
// same rendition's progressive MP4 — one last fallback when HLS won't play.
const HLS_PLAYLIST_RE = /\/(\d+)p\/video\.m3u8$/
const toMp4Fallback = (url) => (HLS_PLAYLIST_RE.test(url || '') ? url.replace(HLS_PLAYLIST_RE, '/play_$1p.mp4') : '')
const usingMp4Fallback = ref(false)
// Token-free path identifying the underlying file, to tell a remint (same
// file, new token) from a version switch that lands mid-remint.
const sourcePath = (url) => (url || '').split('?')[0].replace(/\/bcdn_token=[^/]*/, '').replace(HLS_PLAYLIST_RE, '').replace(/\/play_\d+p\.mp4$/, '')
function tryMp4Fallback(reason) {
  const mp4 = !usingMp4Fallback.value && toMp4Fallback(currentUrl.value)
  if (!mp4) return false
  usingMp4Fallback.value = true
  cdnRetryCount.value = 0
  remintCount.value = 0
  loadError.value = false
  isBuffering.value = false
  const resumeTime = videoRef.value && Number.isFinite(videoRef.value.currentTime) ? videoRef.value.currentTime : 0
  pendingSeek.value = { time: resumeTime, wasPlaying: props.isPlaying }
  trail('mp4-fallback', reason)
  logger.warn('[ReviewMediaViewer] HLS failed — falling back to MP4', { ...getVideoDiagnostics(), reason })
  currentUrl.value = mp4
  nextTick(() => reloadSource())
  return true
}
let rafId = null

// ── Playback-line diagnostics ───────────────────────────────────────────────
// The timeline ("playback line") stays hidden until a usable duration lands
// (ReviewTimeline gates on duration > 0), and the playhead only moves while we
// surface timeupdates. Two watchdogs report when either silently fails so a
// client-only "no playback line" report leaves a diagnostic behind. They log via
// logger.error on purpose: at prod's ERROR level warn/info never reach the
// console interceptor, so only error-level survives to the errorLogs collection
// (which already dedups + rate-limits, so a steady fault won't storm).
const DURATION_WATCHDOG_MS = 6000
const PLAYHEAD_WATCHDOG_MS = 5000
const durationEmitted = ref(false)  // a usable duration reached the timeline
let lastSurfacedTime = 0            // highest currentTime surfaced to the playhead
let durationWatchdogTimer = null
let playheadWatchdogTimer = null

// Set when the pristine-original preview itself can't be decoded (e.g. a
// ProRes/HEVC upload). Forces the blocking "Processing…" overlay back on in
// place of a broken preview. Reset when the source changes (new clip or the
// swap to the encoded variant).
const fallbackDecodeFailed = ref(false)

// True while a Bunny-backed version is still transcoding AND we have no
// playable source to show — drives the full-cover "Processing…" overlay.
//
// The deterministic "still encoding" check is "has Bunny video AND no
// mp4Variants yet" — that directly implies the encoded variant isn't ready
// regardless of what the status field claims. The bunnyStatus string check is
// a secondary guard for any caller that didn't set hasBunnyVideo/
// hasBunnyVariants on the item.
//
// While encoding, the parent now hands us the pristine original as a preview
// URL (getBunnyReviewVideoUrl returns it when mp4Variants is empty), so we
// PLAY it instead of blocking — the overlay only takes over when there's no
// URL at all or that preview failed to decode.
// Server-side truth: is Bunny still working on this item? Note that
// onBunnyStreamReady deliberately pins bunnyStatus to 'encoding' when the
// mp4Variants record exists but the file hasn't landed on the CDN edge yet —
// so this stays true even with hasBunnyVariants set.
const stillEncoding = computed(() => {
  const item = props.item
  if (!item) return false
  return !!(
    (item.hasBunnyVideo && !item.hasBunnyVariants) ||
    (item.bunnyStatus &&
      item.bunnyStatus !== BUNNY_STREAM_STATUS.READY &&
      item.bunnyStatus !== BUNNY_STREAM_STATUS.FAILED)
  )
})

const isTranscoding = computed(() => {
  if (!stillEncoding.value) return false
  return !currentUrl.value || fallbackDecodeFailed.value
})

// True while we're playing the pristine original as a preview because the HD
// transcode hasn't produced a variant yet. Drives the processing banner.
const isProcessingPreview = computed(() => {
  const item = props.item
  if (!item) return false
  if (fallbackDecodeFailed.value) return false
  // stillEncoding, not !hasBunnyVariants: the variant record can exist before
  // the file is on the CDN, and the server serves the original in that window too.
  return !!currentUrl.value && stillEncoding.value
})

// J/K/L shuttle transport + frame stepping (arrows and ,/.). The getter
// returns null for images and errored videos so the keys pass through.
const { shuttleSpeed, isShuttling, isReversing, stopShuttle } = useVideoShuttle(
  () => (props.item?.type === 'video' && props.item?.url && !loadError.value ? videoRef.value : null),
  {
    arrowFrameStep: true,
    // No fps metadata exists for review items today — fall back to 24fps
    // (matches the timecode base in xmlUtils and typical delivered content).
    // Honors item fps/frameRate if a future pipeline adds it.
    getFps: () => props.item?.fps || props.item?.frameRate || 24,
    // Restore the user's timeline-selected speed when leaving shuttle mode.
    getBaseRate: () => props.playbackRate,
  }
)

// ── Playback event trail ────────────────────────────────────────────────────
// Ring buffer of recent media events for the current source, attached to every
// diagnostic log. Prod runs at ERROR level so the info-level retry logs never
// reach errorLogs; the trail is how a triaged error shows what led up to it.
const TRAIL_MAX = 30
let playbackTrail = []
let trailStart = Date.now()
function trail(event, extra) {
  const v = videoRef.value
  const elapsed = ((Date.now() - trailStart) / 1000).toFixed(1)
  const t = v && Number.isFinite(v.currentTime) ? v.currentTime.toFixed(1) : '-'
  playbackTrail.push(`+${elapsed}s ${event} t=${t} rs=${v?.readyState ?? '-'} ns=${v?.networkState ?? '-'}${extra ? ` ${extra}` : ''}`)
  if (playbackTrail.length > TRAIL_MAX) playbackTrail.shift()
}
function resetTrail() {
  playbackTrail = []
  trailStart = Date.now()
}

// Stall watchdog: playing but stuck buffering this long → user sees an endless
// spinner with no media error. Chrome can park a progressive download (network
// IDLE, buffer short of the playhead) and never re-request — one reload at the
// current position re-issues the range fetch. Log only if that also stalls.
const STALL_WATCHDOG_MS = 15000
let stallTimer = null
let stallHealthyTimer = null
const STALL_HEALTHY_RESET_MS = 60000
const stallReloaded = ref(false)
watch(isBuffering, (buffering) => {
  clearTimeout(stallTimer)
  clearTimeout(stallHealthyTimer)
  if (!buffering) {
    // A minute of clean playback after a recovered stall earns a fresh reload.
    if (stallReloaded.value) stallHealthyTimer = setTimeout(() => { stallReloaded.value = false }, STALL_HEALTHY_RESET_MS)
    return
  }
  stallTimer = setTimeout(() => {
    if (!isBuffering.value || !props.isPlaying || loadError.value || awaitingRemint.value) return
    // An expired token 403s every segment — a same-URL reload can't help.
    if (currentUrl.value && isUrlExpired(currentUrl.value) && remintCount.value < MAX_REMINTS) {
      trail('stall-remint')
      requestRemint(true)
      return
    }
    if (!stallReloaded.value && videoRef.value && currentUrl.value) {
      stallReloaded.value = true
      pendingSeek.value = { time: videoRef.value.currentTime || 0, wasPlaying: true }
      trail('stall-reload')
      logger.warn('[ReviewMediaViewer] Playback stalled — reloading at current position', getVideoDiagnostics())
      reloadSource()
      return
    }
    if (tryMp4Fallback('stall')) return
    logger.error('[ReviewMediaViewer] Playback stalled — buffering > 15s', getVideoDiagnostics())
  }, STALL_WATCHDOG_MS)
})

// play() rejections other than teardown aborts / autoplay policy mean the
// element refused the source. NotSupportedError is the element rejecting a
// source that failed to load — onVideoError owns that recovery and logs at
// error if it ends terminal, so it's a warn here (else every recovered
// expired-token load logs a false error).
function logPlayFailure(label, err) {
  if (err.name === 'AbortError') return
  const log = ['NotAllowedError', 'NotSupportedError'].includes(err.name) ? logger.warn : logger.error
  log(`[ReviewMediaViewer] ${label}`, { ...getVideoDiagnostics(), playError: `${err.name}: ${err.message}` })
}

// HTMLMediaElement.error codes — kept explicit so the logged code is readable.
const MEDIA_ERR_NETWORK = 2 // request died mid-stream (fetch/range failure)
const MEDIA_ERR_DECODE = 3 // bytes present but undecodable (corrupt/truncated)

const NETWORK_STATE_LABELS = ['EMPTY', 'IDLE', 'LOADING', 'NO_SOURCE']
const READY_STATE_LABELS = [
  'HAVE_NOTHING', 'HAVE_METADATA', 'HAVE_CURRENT_DATA',
  'HAVE_FUTURE_DATA', 'HAVE_ENOUGH_DATA',
]

// Snapshot everything useful for pinpointing WHY playback died. The decisive
// fields are `failedAtTime` (the exact timestamp) and `buffered` — if the
// failure point sits at a buffered-range edge it's a network/range fetch
// problem; if it's well inside a buffered range it's decode/corruption.
function getVideoDiagnostics() {
  const v = videoRef.value
  const mediaError = v?.error
  const buffered = []
  try {
    if (v?.buffered) {
      for (let i = 0; i < v.buffered.length; i++) {
        buffered.push({
          start: Number(v.buffered.start(i).toFixed(2)),
          end: Number(v.buffered.end(i).toFixed(2)),
        })
      }
    }
  } catch {
    // buffered access can throw on a torn-down element — ignore
  }
  let urlHost = ''
  let urlFile = ''
  try {
    const u = currentUrl.value ? new URL(currentUrl.value) : null
    urlHost = u?.host || ''
    // Last path segment only — signed tokens can live in the path or query.
    urlFile = u?.pathname.split('/').pop() || ''
  } catch {
    urlHost = 'invalid-url'
  }
  return {
    reviewId: props.item?._reviewId || null,
    itemId: props.item?.id || null,
    itemName: props.item?.name || null,
    mediaErrorCode: mediaError?.code || null,
    mediaErrorMessage: mediaError?.message || null,
    failedAtTime: v && Number.isFinite(v.currentTime) ? Number(v.currentTime.toFixed(2)) : null,
    duration: v && Number.isFinite(v.duration) ? Number(v.duration.toFixed(2)) : null,
    networkState: v ? `${v.networkState} (${NETWORK_STATE_LABELS[v.networkState] ?? '?'})` : null,
    readyState: v ? `${v.readyState} (${READY_STATE_LABELS[v.readyState] ?? '?'})` : null,
    buffered,
    urlHost,
    // Bunny diagnostics — to debug cases where isTranscoding should have
    // suppressed the <video> element but didn't.
    bunnyStatus: props.item?.bunnyStatus || null,
    hasBunnyVideo: props.item?.hasBunnyVideo || false,
    hasBunnyVariants: props.item?.hasBunnyVariants || false,
    isTranscoding: isTranscoding.value,
    stillEncoding: stillEncoding.value,
    // Without these the final log reads as "no retry ran" when up to 3 reloads
    // and 2 remints already burned.
    cdnRetryCount: cdnRetryCount.value,
    remintCount: remintCount.value,
    urlFile,
    urlExpiresInSec: urlExpiresInSec(currentUrl.value),
    usingMp4Fallback: usingMp4Fallback.value,
    needsFreshUrl: needsFreshUrl.value,
    hlsJs: usesHlsJs.value,
    hasUrl: !!currentUrl.value,
    isProcessingPreview: isProcessingPreview.value,
    fallbackDecodeFailed: fallbackDecodeFailed.value,
    isPlaying: !!props.isPlaying,
    isBuffering: isBuffering.value,
    hasStarted: hasStarted.value,
    awaitingRemint: awaitingRemint.value,
    itemDuration: props.item?.duration ?? null,
    videoSize: v?.videoWidth ? `${v.videoWidth}x${v.videoHeight}` : null,
    pageVisible: typeof document !== 'undefined' ? document.visibilityState : null,
    online: typeof navigator !== 'undefined' ? navigator.onLine : null,
    canPlayH264: v?.canPlayType?.('video/mp4; codecs="avc1.640028"') || '',
    canPlayHevc: v?.canPlayType?.('video/mp4; codecs="hvc1.1.6.L93.B0"') || '',
    trail: playbackTrail.join('\n'),
  }
}

async function onVideoError() {
  const code = videoRef.value?.error?.code || null
  const diagnostics = getVideoDiagnostics()

  // Original preview isn't browser-decodable (e.g. a ProRes/HEVC upload). We
  // can't fix that by retrying — the transcoded H.264 variant will arrive
  // shortly. Drop the preview and fall back to the "Processing…" overlay
  // instead of surfacing a "damaged file" error for a file that's fine.
  if (isProcessingPreview.value && code === MEDIA_ERR_DECODE) {
    fallbackDecodeFailed.value = true
    loadError.value = false
    isBuffering.value = false
    logger.warn('[ReviewMediaViewer] Original preview undecodable while transcoding — awaiting encoded variant', diagnostics)
    return
  }

  // Still transcoding: the encoded variant simply isn't ready yet, so a load
  // failure here is expected — not a broken file. Burning the retry/remint
  // budget can't conjure a variant that doesn't exist, and an error-level log
  // would be a false alarm. Show the "Processing…" overlay and bail; the item
  // watcher reloads the source once the parent pushes the ready URL.
  //
  // Gated on stillEncoding, NOT isTranscoding: when the variant record exists
  // but hasn't reached the CDN edge we DO have a URL, so isTranscoding is false
  // while the URL is still unservable. Reminting only re-signs the same missing
  // file, so every retry is doomed. Flip fallbackDecodeFailed to engage the
  // overlay; the item watcher clears it when the ready URL lands.
  if (stillEncoding.value) {
    fallbackDecodeFailed.value = true
    loadError.value = false
    isBuffering.value = false
    logger.warn('[ReviewMediaViewer] Video errored while still transcoding — awaiting encoded variant', diagnostics)
    return
  }

  // A DECODE error on a Bunny MP4 is almost never a bad file. Oct 2026 (Lori /
  // GDEP): Chrome's cache held wrong-offset bytes for a file that was fine at
  // origin (source — Bunny edge vs Chrome's cache — unproven; see
  // checkDecodeBytes), and Bunny's `max-age=2592000` keeps them for 30 days.
  // Every same-URL reload re-reads that cache and fails identically. A
  // re-minted URL has a new `expires`, so it's a new cache key — handled by the
  // remint branch below, which decode errors now share.
  const isDecodeError = code === MEDIA_ERR_DECODE

  // Bunny source with no usable variants: transcoding never produced a playable
  // file (or the item was never fully migrated). Retrying the URL or reminting a
  // token can't conjure variants that don't exist — both paths would burn a ~13s
  // retry cycle ending in a doomed refresh-url the parent silently can't service.
  // Surface "unavailable" immediately instead.
  // Bunny gave up encoding this version — no retry or remint can produce a
  // playable file, so say so immediately instead of a ~13s doomed retry cycle.
  if (props.item?.bunnyStatus === BUNNY_STREAM_STATUS.FAILED) {
    loadError.value = true
    isBuffering.value = false
    errorKind.value = 'failed'
    logger.error('[ReviewMediaViewer] Transcode failed — no playable variant', diagnostics)
    return
  }

  if (!props.item?.hasBunnyVariants) {
    loadError.value = true
    isBuffering.value = false
    errorKind.value = 'generic'
    logger.error('[ReviewMediaViewer] No playable source — media unavailable', diagnostics)
    return
  }

  // A non-decode
  // failure is almost always a CDN edge that hasn't pulled the freshly-encoded
  // variant yet. Reload the same (still-valid) signed URL after a backoff — the
  // edge warms within a few seconds — restoring playback position so recovery
  // is seamless. This replaces the manual page-refresh the user was doing.
  // Skipped when the token has expired: the same dead URL 403s every time.
  // Nobody watching (paused, hidden tab): mint on return instead of now.
  if (currentUrl.value && shouldDeferRemint()) {
    deferRemint()
    return
  }

  // A freshly signed URL that 404s means the file isn't on Bunny — every
  // retry/remint re-signs the same missing file. Probe once up front and go
  // straight to the MP4 fallback (or the overlay).
  if (currentUrl.value && cdnRetryCount.value === 0 && remintCount.value === 0 && !isUrlExpired(currentUrl.value)) {
    const edge = await probeCdnEdge(currentUrl.value)
    if (edge?.startsWith('404')) {
      trail('cdn-404')
      if (tryMp4Fallback('404')) return
      // Keep the cheap same-URL retries (a just-encoded file can 404 on a cold
      // edge); only reminting is pointless against a missing file.
      remintCount.value = MAX_REMINTS
    }
  }

  if (currentUrl.value && !isDecodeError && cdnRetryCount.value < MAX_CDN_RETRIES && !isUrlExpired(currentUrl.value)) {
    cdnRetryCount.value += 1
    const resumeTime = videoRef.value && Number.isFinite(videoRef.value.currentTime)
      ? videoRef.value.currentTime
      : 0
    const wasPlaying = props.isPlaying
    loadError.value = false
    isBuffering.value = false
    pendingSeek.value = { time: resumeTime, wasPlaying }
    logger.info('[ReviewMediaViewer] CDN load failed — retrying same URL', {
      ...diagnostics,
      attempt: cdnRetryCount.value,
      resumeTime: Number(resumeTime.toFixed(2)),
    })
    const backoffMs = 800 * cdnRetryCount.value
    trail('cdn-retry', `#${cdnRetryCount.value}`)
    setTimeout(() => {
      if (videoRef.value && currentUrl.value) reloadSource()
    }, backoffMs)
    return
  }

  // Bunny source, same-URL CDN reloads exhausted (or skipped — token already
  // expired): the URL itself is dead. Ask the parent to mint a fresh one.
  if (currentUrl.value && remintCount.value < MAX_REMINTS) {
    if (isDecodeError && remintCount.value === 0 && /\.mp4(\?|$)/.test(currentUrl.value)) checkDecodeBytes(currentUrl.value, diagnostics)
    requestRemint(props.isPlaying)
    return
  }

  if (tryMp4Fallback('terminal')) return

  loadError.value = true
  isBuffering.value = false
  errorKind.value = isDecodeError ? 'decode' : 'generic'
  const reason = isDecodeError
    ? 'decode error — persisted across fresh signed URLs'
    : code === MEDIA_ERR_NETWORK
      ? 'network error — transport/range fetch failed'
      : 'unrecoverable media error'
  // Which CDN edge served this file (CORS exposes only `Server`) — decode
  // failures on a clean file point at a bad edge copy, so name the edge.
  const cdnEdge = currentUrl.value ? await probeCdnEdge(currentUrl.value) : null
  // Error level for every terminal failure (incl. decode) so it lands in errorLogs.
  logger.error(`[ReviewMediaViewer] Video failed to load (${isDecodeError ? 'decode' : 'load'})`, { ...diagnostics, reason, cdnEdge })
}

// Signed Bunny URLs carry `expires` (unix seconds). A tab left open past the
// 2h TTL holds a dead URL — Bunny 403s it, which the element reports as a
// format error. 30s of slack so a URL about to die isn't handed to load().
function urlExpiresInSec(url) {
  try {
    // Query form, or Bunny's path-token form (/bcdn_token=…&expires=N/…) on HLS URLs.
    const expires = Number(new URL(url).searchParams.get('expires') || (url.match(/[?&]expires=(\d+)/) || [])[1])
    return expires > 0 ? Math.round(expires - Date.now() / 1000) : null
  } catch {
    return null
  }
}
function isUrlExpired(url) {
  const left = urlExpiresInSec(url)
  return left !== null && left < 30
}

// Ask the parent to mint a fresh signed URL. Stash position now so the
// position-restoring reload (triggered by the incoming props.item.url change)
// resumes where we left off.
function requestRemint(wasPlaying) {
  remintCount.value += 1
  awaitingRemint.value = true
  const resumeTime = videoRef.value && Number.isFinite(videoRef.value.currentTime)
    ? videoRef.value.currentTime
    : 0
  pendingSeek.value = { time: resumeTime, wasPlaying }
  loadError.value = false
  isBuffering.value = true
  logger.info('[ReviewMediaViewer] Requesting fresh signed URL', {
    ...getVideoDiagnostics(),
    attempt: remintCount.value,
    resumeTime: Number(resumeTime.toFixed(2)),
  })
  clearTimeout(remintTimer)
  remintTimer = setTimeout(() => {
    if (!awaitingRemint.value) return // a fresh URL arrived; the reload handled it
    awaitingRemint.value = false
    // Hidden tab: the callable may simply not have run (throttled timers,
    // network waking up). Re-ask when the user comes back.
    if (shouldDeferRemint()) {
      deferRemint()
      return
    }
    loadError.value = true
    isBuffering.value = false
    errorKind.value = 'generic'
    // Re-read diagnostics here — the ones captured at request time are 30s
    // stale, which made every one of these logs read as "no retry ran".
    logger.error('[ReviewMediaViewer] Fresh URL request went unanswered — surfacing error', getVideoDiagnostics())
  }, REMINT_TIMEOUT_MS)
  trail('remint-request', `#${remintCount.value}`)
  setTimeout(() => emit('refresh-url'), Math.max(0, lastRemintArrivedAt + 1100 - Date.now()))
}

// Decode errors on Bunny MP4s: Chrome's cache has held wrong-offset bytes for a
// file that is fine at origin, but whether Bunny sent them or Chrome's cache
// mis-assembled them is unproven. Compare a window around the failure point as
// the browser has it (default cache) vs straight from Bunny (no-store) and log
// the outcome either way — a mismatch names the bad bytes, a match clears them.
// ponytail: one 4 MiB window around the playhead estimate; the bad span can sit elsewhere in the file.
const BYTE_CHECK_WINDOW = 4 * 1024 * 1024
async function checkDecodeBytes(url, diagnostics) {
  // Read before any await: the remint reload that follows resets the element.
  const duration = videoRef.value?.duration
  const time = videoRef.value?.currentTime || 0
  try {
    const head = await fetch(url, { method: 'HEAD', cache: 'no-store', signal: AbortSignal.timeout(5000) })
    const size = Number(head.headers.get('content-length'))
    if (!size || !Number.isFinite(duration) || !duration) return
    const start = Math.max(0, Math.min(size - BYTE_CHECK_WINDOW, Math.floor(size * time / duration) - BYTE_CHECK_WINDOW / 2))
    const range = `bytes=${start}-${Math.min(size, start + BYTE_CHECK_WINDOW) - 1}`
    const get = (cache) => fetch(url, { cache, headers: { Range: range }, signal: AbortSignal.timeout(30000) })
      .then(async (r) => ({
        server: r.headers.get('server'),
        status: r.status,
        // Readable only once the pull zone lists them in Access-Control-Expose-Headers.
        // A cache hit replays the stored headers, so cached.requestId names the
        // Bunny response that put those bytes in the browser.
        requestId: r.headers.get('cdn-requestid'),
        contentRange: r.headers.get('content-range'),
        cachedAt: r.headers.get('cdn-cachedat'),
        bytes: new Uint8Array(await r.arrayBuffer()),
      }))
    const [cached, fresh] = await Promise.all([get('default'), get('no-store')])
    const hex = (u8) => [...u8].map((b) => b.toString(16).padStart(2, '0')).join('')
    let i = 0
    while (i < cached.bytes.length && cached.bytes[i] === fresh.bytes[i]) i++
    const result = { ...diagnostics, range, size, cachedServer: cached.server, freshServer: fresh.server, cachedStatus: cached.status, freshStatus: fresh.status, cachedLen: cached.bytes.length, freshLen: fresh.bytes.length, cachedRequestId: cached.requestId, freshRequestId: fresh.requestId, cachedContentRange: cached.contentRange, freshContentRange: fresh.contentRange, cachedCachedAt: cached.cachedAt, freshCachedAt: fresh.cachedAt }
    if (i === cached.bytes.length && cached.bytes.length === fresh.bytes.length) {
      logger.error('[ReviewMediaViewer] Decode byte check: browser bytes MATCH Bunny', result)
      return
    }
    const probe = hex(cached.bytes.subarray(i, i + 32))
    const found = hex(fresh.bytes).indexOf(probe)
    logger.error('[ReviewMediaViewer] Decode byte check: browser bytes DIFFER from Bunny', {
      ...result,
      firstDiffAt: start + i,
      browserBytes: hex(cached.bytes.subarray(i, i + 16)),
      bunnyBytes: hex(fresh.bytes.subarray(i, i + 16)),
      browserBytesBelongAt: found >= 0 && found % 2 === 0 ? start + found / 2 : null,
    })
  } catch (err) {
    logger.warn('[ReviewMediaViewer] Decode byte check failed', { error: err.message })
  }
}

async function probeCdnEdge(url) {
  try {
    const res = await fetch(url, { headers: { Range: 'bytes=0-0' }, signal: AbortSignal.timeout(3000) })
    return `${res.status} ${res.headers.get('server') || '?'} len=${res.headers.get('content-length') || '?'}`
  } catch (err) {
    return `probe failed: ${err.name}`
  }
}

function onTimeUpdate() {
  if (rafId) return
  rafId = requestAnimationFrame(() => {
    // While probing an Infinity-duration file we deliberately seek to the end;
    // don't surface that throwaway position to the timeline/playhead.
    if (videoRef.value && !needsProbeReset.value) {
      lastSurfacedTime = videoRef.value.currentTime
      emit('timeupdate', videoRef.value.currentTime)
    }
    rafId = null
  })
}

// Resolve the element's duration into a usable value for the timeline. Handles
// the three ways the native element fails us: a non-finite/zero duration (fall
// back to the duration stored at upload time), and Infinity (probe to the end
// to force the browser to compute it). Wired to both loadedmetadata and
// durationchange so a late-resolving duration is still caught.
// Single choke point for "a usable duration is now known" so the duration
// watchdog can tell resolved-late from never-resolved.
function emitUsableDuration(d) {
  durationEmitted.value = true
  emit('duration-change', d)
}

function emitDurationIfUsable() {
  const v = videoRef.value
  if (!v) return
  const d = v.duration

  if (Number.isFinite(d) && d > 0) {
    // The probe seeks to the end; once a real duration lands, restore position.
    if (needsProbeReset.value) finishProbe('durationchange')
    emitUsableDuration(d)
    return
  }

  if (d === Infinity && !hasProbedInfinity.value) {
    hasProbedInfinity.value = true
    needsProbeReset.value = true
    probeResumeTime = Number.isFinite(v.currentTime) ? v.currentTime : 0
    try {
      v.currentTime = 1e101
      // Safety net: if neither a finite durationchange nor seeked ever lands,
      // unfreeze the playhead rather than suppressing timeupdates forever.
      probeTimer = setTimeout(() => finishProbe('timeout'), PROBE_TIMEOUT_MS)
    } catch {
      needsProbeReset.value = false
    }
    return
  }

  // NaN / 0 / an Infinity we couldn't probe away: fall back to the duration we
  // captured at upload time so the timeline is still usable (covers mobile
  // Safari deferring metadata until play).
  const stored = props.item?.duration
  if (Number.isFinite(stored) && stored > 0) {
    emitUsableDuration(stored)
    logger.info('[ReviewMediaViewer] Using stored duration fallback', {
      ...getVideoDiagnostics(),
      storedDuration: stored,
    })
  }
}

// Report when a video has had time to load but no usable duration ever reached
// the timeline — i.e. the playback line is hidden. Skips the Bunny-transcoding
// state (a "Processing…" placeholder shows, no timeline expected) and the
// load-error state — when the media failed outright, onVideoError already
// surfaced *why* the duration is missing, so firing here too just double-logs
// the same dead item. The watchdog's real job is the SILENT case: a video that
// loads and plays but never emits a usable duration.
function armDurationWatchdog() {
  clearTimeout(durationWatchdogTimer)
  durationWatchdogTimer = null
  if (props.item?.type !== 'video') return
  durationWatchdogTimer = setTimeout(() => {
    if (durationEmitted.value || isTranscoding.value || !currentUrl.value || loadError.value) return
    // The watchdog's real target is the SILENT case: a video that reached
    // metadata yet never emitted a usable duration. A still-HAVE_NOTHING element
    // hasn't loaded at all — that's a slow/stalled load, not a duration bug, and
    // onVideoError covers it if the load ultimately fails. Log it, but at warn so
    // it doesn't masquerade as a confirmed playback break.
    const metadataLoaded = (videoRef.value?.readyState ?? 0) >= 1
    const log = metadataLoaded ? logger.error : logger.warn
    log('[ReviewPlayer] Playback line hidden — no usable duration resolved', {
      ...getVideoDiagnostics(),
      storedItemDuration: Number.isFinite(props.item?.duration) ? props.item.duration : null,
      needsProbeReset: needsProbeReset.value,
      hasProbedInfinity: hasProbedInfinity.value,
    })
  }, DURATION_WATCHDOG_MS)
}

// Report when the <video> element is genuinely advancing but no moving position
// is reaching the playhead — i.e. the line is stuck at the start while the video
// plays. Captures whatever element/probe state is true at that moment.
function armPlayheadWatchdog() {
  clearTimeout(playheadWatchdogTimer)
  playheadWatchdogTimer = setTimeout(() => {
    const v = videoRef.value
    if (!v || v.paused) return
    const elementAdvancing = Number.isFinite(v.currentTime) && v.currentTime > 2
    const surfacedStuck = lastSurfacedTime <= 0.5
    if (elementAdvancing && surfacedStuck) {
      // warn, not error: purely diagnostic — playback continues and there is no
      // recovery path here; RAF batching under main-thread load can also
      // false-positive this (element advances while surfacing lags a frame)
      logger.warn('[ReviewPlayer] Playhead frozen — video advancing but position not surfaced', {
        ...getVideoDiagnostics(),
        elementCurrentTime: Number(v.currentTime.toFixed(2)),
        lastSurfacedTime: Number(lastSurfacedTime.toFixed(2)),
        durationEmitted: durationEmitted.value,
        needsProbeReset: needsProbeReset.value,
        hasProbedInfinity: hasProbedInfinity.value,
      })
    }
  }, PLAYHEAD_WATCHDOG_MS)
}

// The probe seek landed but the duration is still Infinity — a finite
// durationchange is never coming for this load. Unfreeze and fall back.
function onSeeked() {
  if (needsProbeReset.value) finishProbe('seeked')
}

function finishProbe(source) {
  if (!needsProbeReset.value) return
  needsProbeReset.value = false
  clearTimeout(probeTimer)
  probeTimer = null
  const v = videoRef.value
  if (v) {
    try {
      v.currentTime = probeResumeTime
    } catch {
      // not seekable — position stays where the probe left it; timeupdates resume regardless
    }
  }
  if (source !== 'durationchange') {
    const stored = props.item?.duration
    if (Number.isFinite(stored) && stored > 0) emitUsableDuration(stored)
    logger.warn('[ReviewMediaViewer] Infinity probe did not resolve; using fallback', {
      ...getVideoDiagnostics(),
      source,
      storedDuration: Number.isFinite(stored) ? stored : null,
    })
  }
}

function onLoadedMetadata() {
  const v = videoRef.value
  if (!v) return
  v.volume = props.volume
  v.muted = props.isMuted
  v.playbackRate = props.playbackRate
  emitDurationIfUsable()

  // After a URL refresh, jump back to where the user was and resume if they
  // had been playing — so recovery is seamless instead of a restart from 0.
  if (pendingSeek.value) {
    const { time, wasPlaying } = pendingSeek.value
    pendingSeek.value = null
    if (needsProbeReset.value) {
      // emitDurationIfUsable (above) just launched the probe — let finishProbe
      // restore to the deferred position instead of seeking now.
      probeResumeTime = time
    } else if (time > 0) {
      try {
        // Clamp only when the element knows its duration; an Infinity/NaN
        // duration must not drop a deferred seek — that's what created it.
        v.currentTime = Number.isFinite(v.duration) && v.duration > 0
          ? Math.min(time, v.duration)
          : time
      } catch {
        // setting currentTime can throw if not seekable yet — ignore
      }
    }
    if (wasPlaying) {
      v.play().catch((err) => logPlayFailure('Resume after refresh failed', err))
    }
  }
}

// The shuttle starts playback directly on the element (bypassing
// togglePlayback), so the poster teardown has to live on the play event.
function onPlay() {
  hasStarted.value = true
  armPlayheadWatchdog()
  emit('play')
}

// Pressing play on a tab left open past the token TTL: mint first instead of
// handing the element a URL Bunny will 403 (it reports that as a format error).
function startPlayback() {
  if (awaitingRemint.value) return // fresh URL in flight; pendingSeek resumes play
  if (needsFreshUrl.value) {
    resumeDeferredRemint(true)
    return
  }
  if (isUrlExpired(currentUrl.value) && remintCount.value < MAX_REMINTS) {
    requestRemint(true)
    return
  }
  videoRef.value.play().catch((err) => logPlayFailure('Play failed', err))
}

function togglePlayback() {
  if (props.item.type !== 'video' || !videoRef.value || !props.item.url) return
  if (isShuttling.value) {
    // A click while shuttling (either direction) acts as a hard stop.
    stopShuttle({ pause: true })
    return
  }
  if (props.isPlaying) {
    videoRef.value.pause()
  } else {
    hasStarted.value = true
    startPlayback()
  }
}

function seekTo(time) {
  const v = videoRef.value
  if (!v) return
  if (!Number.isFinite(time)) {
    logger.warn('[ReviewMediaViewer] Rejected non-finite seek', {
      ...getVideoDiagnostics(),
      requestedTime: String(time), // String() — JSON.stringify drops Infinity/NaN
    })
    return
  }
  const elementDuration = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : null
  const storedDuration = Number.isFinite(props.item?.duration) && props.item.duration > 0
    ? props.item.duration
    : null
  const max = elementDuration ?? storedDuration
  const clamped = Math.max(0, max !== null ? Math.min(time, max) : time)

  // An Infinity probe is in flight — don't fight its seek; retarget the
  // restore position so the user's click wins once the probe settles.
  if (needsProbeReset.value) {
    probeResumeTime = clamped
    emit('timeupdate', clamped)
    return
  }

  // Element can't seek yet (no metadata) — stash instead of silently losing
  // the click; onLoadedMetadata applies it.
  if (v.readyState === 0) {
    pendingSeek.value = { time: clamped, wasPlaying: false }
    emit('timeupdate', clamped)
    logger.warn('[ReviewMediaViewer] Deferred seek until metadata loads', {
      ...getVideoDiagnostics(),
      requestedTime: clamped,
    })
    return
  }

  try {
    v.currentTime = clamped
    emit('timeupdate', clamped)
  } catch (err) {
    logger.warn('[ReviewMediaViewer] Seek failed', {
      ...getVideoDiagnostics(),
      requestedTime: clamped,
      error: err.message,
    })
  }
}

watch(() => props.isPlaying, (playing) => {
  if (!videoRef.value || !props.item.url) return
  if (playing) {
    // The timeline play button while rewinding means "play forward" —
    // cancel the reverse loop before starting the element.
    if (isReversing.value) stopShuttle()
    hasStarted.value = true
    startPlayback()
  } else {
    // Reverse keeps the element paused while it shuttles, so a pause-state
    // echo from our own emit('pause') must not cancel it. A real pause
    // request during a forward shuttle resets the speed override.
    if (isShuttling.value && !isReversing.value) stopShuttle()
    videoRef.value.pause()
    clearTimeout(playheadWatchdogTimer)
  }
})

watch(() => props.volume, (val) => {
  if (videoRef.value) videoRef.value.volume = val
})

watch(() => props.isMuted, (val) => {
  if (videoRef.value) videoRef.value.muted = val
})

watch(() => props.playbackRate, (val) => {
  // While shuttling, the shuttle owns the element's rate; the new base
  // rate is applied when the shuttle stops (via getBaseRate).
  if (isShuttling.value) return
  if (videoRef.value) videoRef.value.playbackRate = val
})

watch(() => props.item, (newItem, oldItem) => {
  stopShuttle()
  hasStarted.value = false
  clearTimeout(playheadWatchdogTimer) // a stale check must not fire against the new item
  if (newItem?.url !== oldItem?.url) {
    needsFreshUrl.value = false
    if (awaitingRemint.value && sourcePath(newItem?.url) === sourcePath(oldItem?.url)) {
      // Re-mint reload (Issue 9): the parent answered a 'refresh-url' emit with
      // a fresh signed URL for the SAME clip (it rebuilds the item object, so
      // this generic watcher — not a separate url watcher — is what fires). Swap
      // the source and reload, but PRESERVE pendingSeek (stashed before the emit)
      // so playback resumes where the token died. cdnRetryCount resets so the
      // fresh URL earns its own cold-edge retries; remintCount is kept to hold
      // the emit → remint → fail → emit loop cap.
      awaitingRemint.value = false
      clearTimeout(remintTimer)
      remintTimer = null
      loadError.value = false
      // Already on the MP4 fallback: keep it, just with the fresh token.
      currentUrl.value = (usingMp4Fallback.value && toMp4Fallback(newItem?.url)) || newItem?.url || ''
      cdnRetryCount.value = 0
      lastRemintArrivedAt = Date.now()
      trail('remint-url-arrived')
    } else {
      resetTrail()
      trail('source-set', newItem?.url ? '' : '(no url)')
      loadError.value = false
      errorKind.value = 'generic'
      currentUrl.value = newItem?.url || ''
      // A new source (new clip, or the swap from original preview → encoded
      // variant) gets a clean slate: a prior preview-decode failure must not
      // suppress the real variant.
      fallbackDecodeFailed.value = false
      usingMp4Fallback.value = false
      cdnRetryCount.value = 0
      remintCount.value = 0
      stallReloaded.value = false
      awaitingRemint.value = false
      clearTimeout(remintTimer)
      remintTimer = null
      pendingSeek.value = null
      hasProbedInfinity.value = false
      needsProbeReset.value = false
      clearTimeout(probeTimer)
      probeTimer = null
      probeResumeTime = 0
      // New source: nothing has reached the timeline yet. The seed below (or a
      // later loadedmetadata) flips this back to true once a duration lands.
      durationEmitted.value = false
      lastSurfacedTime = 0
      // Seed the timeline from the duration captured at upload time so the bar is
      // usable the instant a video is selected — without waiting on loadedmetadata
      // (which mobile Safari routinely defers until the user presses play).
      if (newItem?.type === 'video' && Number.isFinite(newItem?.duration) && newItem.duration > 0) {
        emitUsableDuration(newItem.duration)
      }
      armDurationWatchdog()
    }
  }

  // The seed above only runs when the URL changes, but on a first load the
  // duration is known BEFORE the URL is: the item object arrives with the
  // version's stored duration and an empty url (the signed Bunny url is a
  // callable round-trip), so that update compares '' to '' and skips the seed.
  // Result: no playback line at all until the url landed — the whole window a
  // client is looking at the poster deciding to press play.
  if (
    !durationEmitted.value &&
    newItem?.type === 'video' &&
    Number.isFinite(newItem?.duration) &&
    newItem.duration > 0
  ) {
    emitUsableDuration(newItem.duration)
  }

  if (videoRef.value) {
    videoRef.value.pause()
    if (newItem?.url !== oldItem?.url) {
      nextTick(() => {
        if (videoRef.value) {
          reloadSource()
        }
      })
    }
  }
}, { immediate: true })

// hls.js needs an explicit attach once the <video> exists (native takes `src`).
watch(videoRef, (el) => {
  if (el && usesHlsJs.value) reloadSource()
})

// hls.js reports unrecoverable errors itself rather than through the element's
// `error` event: route them to the same fresh-URL recovery, then surface.
function onHlsFatal(data) {
  trail('hls-fatal', data?.details || '')
  if (currentUrl.value && shouldDeferRemint()) {
    deferRemint()
    return
  }
  if (currentUrl.value && remintCount.value < MAX_REMINTS) {
    requestRemint(props.isPlaying)
    return
  }
  if (tryMp4Fallback(`hls-fatal ${data?.details || ''}`)) return
  loadError.value = true
  isBuffering.value = false
  errorKind.value = 'generic'
  logger.error('[ReviewMediaViewer] HLS playback failed', {
    ...getVideoDiagnostics(),
    hlsType: data?.type || null,
    hlsDetails: data?.details || null,
  })
}

// Coming back to a tab whose token died while hidden: mint now with a fresh
// budget (the deferral may have happened with retries already spent).
function resumeDeferredRemint(wasPlaying) {
  needsFreshUrl.value = false
  cdnRetryCount.value = 0
  remintCount.value = 0
  requestRemint(wasPlaying)
}
function onVisibilityChange() {
  if (!document.hidden && needsFreshUrl.value && !awaitingRemint.value) resumeDeferredRemint(false)
}
document.addEventListener('visibilitychange', onVisibilityChange)

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange)
  clearTimeout(stallHealthyTimer)
  detachHls?.()
  if (rafId) cancelAnimationFrame(rafId)
  clearTimeout(probeTimer)
  clearTimeout(remintTimer)
  clearTimeout(durationWatchdogTimer)
  clearTimeout(playheadWatchdogTimer)
  clearTimeout(stallTimer)
  if (videoRef.value) {
    videoRef.value.pause()
  }
})

// Expose seekTo for parent-driven scrubbing, and videoElement so consumers
// like the AssetDetail screenshot tool can read currentTime / draw to a
// canvas for the Firebase fallback path.
defineExpose({
  seekTo,
  videoElement: () => videoRef.value,
  get currentTime() {
    return videoRef.value?.currentTime || 0;
  },
})
</script>

<style lang="scss" scoped>
.review-media-viewer {
  width: 100%;
}

.processing-banner {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0.85rem;
  border-radius: 0.5rem;
  background: rgba(255, 179, 0, 0.12);
  border: 1px solid rgba(255, 179, 0, 0.35);
  color: #8a5a00;
  font-size: 0.85rem;
  line-height: 1.35;

  .icon {
    flex: 0 0 auto;
    color: #b37400;
  }

  &__text {
    strong {
      color: #7a4f00;
      font-weight: 600;
    }
  }
}

.media-container {
  // aspect-ratio is set via inline :style from the parent so vertical
  // sources render in portrait without letterboxing. Default 16/9
  // applies when no prop is provided.
  width: 100%;
  border-radius: 0.5rem;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  background: #000;
}

.media-element {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.poster-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  z-index: 1;
  pointer-events: none;
}

.play-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.85);
  transition: transform 0.2s ease;

  &:hover {
    color: #fff;
  }
}

.buffering-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  pointer-events: none;
}

.transcoding-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  text-align: center;
  background: rgba(0, 0, 0, 0.85);
  color: rgba(255, 255, 255, 0.95);
  cursor: default;

  .icon {
    color: rgba(255, 255, 255, 0.85);
    margin-bottom: 0.75rem;
  }

  .transcoding-title {
    font-size: 1rem;
    font-weight: 600;
  }
}

.shuttle-badge {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.65);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.8rem;
  font-weight: 600;
  pointer-events: none;
}

.error-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  text-align: center;
  background: rgba(0, 0, 0, 0.85);
  color: rgba(255, 255, 255, 0.95);
  cursor: default;

  .icon {
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 0.75rem;
  }

  .error-title {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .error-detail {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
    max-width: 32rem;
    line-height: 1.4;
  }
}

</style>
